import { startTransition, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ErrorPageRunner from "./ErrorPageRunner";

const STATUS_CONFIG = {
	404: {
		heading: "This page went out for snacks",
		message:
			"…and never came back. Maybe it's in the break room. We'll show you the way home instead.",
		buttonText: "Take me home",
	},
	500: {
		heading: "Our hamsters need a breather",
		message:
			"The wheel stopped spinning. Give it a sec and we'll get it running again.",
		buttonText: "Reload page",
	},
};

export default function ServerErrorPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const [lives, setLives] = useState(3);
	const [score, setScore] = useState(0);

	const statusCode =
		location.state?.statusCode ??
		(location.pathname === "/error/500" ? 500 : 404); // catch-all (*) and /error/404 show 404
	const config = STATUS_CONFIG[statusCode] ?? STATUS_CONFIG[500];

	const handleAction = () => {
		if (statusCode === 500) {
			window.location.reload();
			return;
		}
		startTransition(() => {
			navigate("/", { replace: true });
		});
	};

	// For 500 errors, render full-screen game with text overlay
	if (statusCode === 500) {
		return (
			<section className="relative flex max-h-screen min-h-screen flex-col overflow-hidden bg-gradient-to-b from-orange-400 via-orange-300 to-orange-500">
				<ErrorPageRunner
					statusCode={statusCode}
					fullScreen
					onLivesChange={setLives}
					onScoreChange={setScore}
				/>
				<div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-start pt-8 sm:pt-12 md:pt-16">
					<div className="flex w-full max-w-2xl flex-col items-center px-4 text-center">
						<h4 className="mb-2 text-xl font-semibold leading-tight text-gray-900 drop-shadow-lg sm:mb-3 sm:text-2xl md:text-3xl">
							{config.heading}
						</h4>
						<p className="mb-4 text-sm text-gray-800 drop-shadow-md sm:mb-5 sm:text-base md:text-lg">
							{config.message}
						</p>
						<div className="flex w-full flex-col items-center">
							<button
								type="button"
								onClick={handleAction}
								className="pointer-events-auto rounded-xl bg-amber-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-amber-600 active:scale-[0.98] sm:px-8 sm:py-3 sm:text-base"
							>
								{config.buttonText}
							</button>
							{statusCode === 500 && (
								<div className="pointer-events-none mt-2 flex w-full max-w-[375px] translate-x-1 flex-col items-end gap-1.5 sm:max-w-[600px] sm:translate-x-8">
									<div className="flex items-center gap-1.5">
										{[...Array(3)].map((_, i) => (
											<svg
												key={i}
												className={`h-4 w-4 sm:h-5 sm:w-5 ${i < lives ? "text-red-500" : "text-gray-400"}`}
												fill="currentColor"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
											</svg>
										))}
									</div>
									<div
										className="flex items-center gap-1.5 drop-shadow-md"
										style={{ color: "#78350f" }}
									>
										<span
											className="h-4 w-4 shrink-0 rounded-full shadow-sm sm:h-5 sm:w-5"
											style={{
												background:
													"radial-gradient(circle at 30% 30%, #fef9c3 0%, #fdba74 40%, #f59e0b 75%, #d97706 100%)",
												boxShadow: "0 0 6px rgba(251, 191, 36, 0.5)",
											}}
											aria-hidden
										/>
										<span className="text-sm font-semibold tabular-nums sm:text-base">
											{score}
										</span>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</section>
		);
	}

	// For 404 errors, render normal layout with modal
	return (
		<section className="flex max-h-screen min-h-screen flex-col overflow-hidden bg-gradient-to-b from-orange-400 via-orange-300 to-orange-500">
			<div className="container mx-auto flex flex-1 flex-col items-center justify-center overflow-hidden px-4 py-3 sm:py-4">
				<div className="mx-auto flex max-h-[90vh] w-full max-w-[840px] flex-col items-center justify-center text-center">
					<div className="w-full overflow-hidden rounded-lg bg-white shadow-2xl">
						<div className="flex flex-col items-center justify-between p-4 sm:p-6">
							<div className="mb-3 flex shrink-0 flex-col items-center sm:mb-4">
								<h4 className="mb-2 text-lg font-semibold leading-tight text-gray-900 sm:mb-2.5 sm:text-xl md:text-2xl">
									{config.heading}
								</h4>
								<p className="mb-3 text-sm text-gray-600 sm:mb-4 sm:text-base">
									{config.message}
								</p>
								<button
									type="button"
									onClick={handleAction}
									className="rounded-xl bg-amber-500 px-5 py-2 text-sm font-semibold text-white shadow-md transition-all hover:bg-amber-600 active:scale-[0.98] sm:px-6 sm:py-2.5 sm:text-base"
								>
									{config.buttonText}
								</button>
							</div>
							<div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden py-2">
								<div className="flex max-w-[500px] items-center justify-center gap-2 sm:gap-4 md:gap-5">
									<img
										src="/images/quest/creatures/gifs/BeamkinSnacks.webp"
										alt=""
										className="h-full w-full object-cover object-center"
										aria-hidden
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
